import React, { useCallback, useEffect, useRef, useState } from 'react';
import Recipe from './Recipe';
import LoadingComponent from './LoadingComponent';
import api from '../api';
import { normalizeRecipeSummaries } from '../utils/recipeSummaries';

function DishInput({ onBack }) {
  const [dishName, setDishName]           = useState('');
  const [recipe, setRecipe]               = useState(null);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState(null);
  const [isRecipeReceived, setIsRecipeReceived] = useState(false);
  const [suggestions, setSuggestions]     = useState([]);
  const isSubmitting = useRef(false);

  const fetchRecipe = useCallback(async (requestedDishName) => {
    if (isSubmitting.current || !requestedDishName.trim()) return;

    isSubmitting.current = true;
    sessionStorage.setItem('pendingRecipeDish', requestedDishName);
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/recipe', { params: { dishName: requestedDishName } });
      sessionStorage.removeItem('pendingRecipeDish');
      if (!res.data) throw new Error('Network response was not ok');
      if (res.data.id == null) throw new Error(res.data.dishName + ' is an ' + res.data.introduction);
      setRecipe(res.data);
      setIsRecipeReceived(true);
    } catch (err) {
      if (err.response?.status !== 401) {
        sessionStorage.removeItem('pendingRecipeDish');
      }
      setError(err.message);
    } finally {
      isSubmitting.current = false;
      setLoading(false);
      setDishName('');
      setSuggestions([]);
    }
  }, []);

  useEffect(() => {
    const pendingDish = sessionStorage.getItem('pendingRecipeDish');
    if (pendingDish && localStorage.getItem('authToken')) {
      setDishName(pendingDish);
      fetchRecipe(pendingDish);
    }
  }, [fetchRecipe]);

  const fetchSuggestions = async (query) => {
    if (!query) { setSuggestions([]); return; }
    try {
      const response = await api.get('/recipe/suggestions', { params: { typedName: query } });
      setSuggestions(normalizeRecipeSummaries(response.data));
    } catch (err) {
      console.error('Error fetching suggestions:', err);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setDishName(value);
    fetchSuggestions(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await fetchRecipe(dishName);
  };

  if (loading) return <LoadingComponent />;

  return (
    <div className="content-panel dish-search-page">
      <button className="back-btn" onClick={onBack}>
        &#8592; Back
      </button>

      <section className="feature-page-banner dish-page-banner">
        <img
          src="https://images.unsplash.com/photo-1565557623262-b51c2513a641?auto=format&fit=crop&w=1000&q=80"
          alt="A freshly cooked, richly spiced Indian curry served with herbs"
          width="1000"
          height="560"
        />
        <div className="feature-page-banner-copy">
          <span className="eyebrow">Already craving something?</span>
          <h1>Let’s make your favourite.</h1>
          <p>Tell us the dish and we’ll bring the recipe to your kitchen.</p>
        </div>
      </section>

      <div className="section-header">
        <h2>Search for a dish</h2>
        <p>We’ll find the ingredients and step-by-step cooking instructions.</p>
      </div>

      {/* Search Form */}
      {!isRecipeReceived && (
        <div className="card-custom" style={{ padding: '32px', marginBottom: '24px', overflow: 'visible' }}>
          <form onSubmit={handleSubmit}>
            <div style={{ position: 'relative', marginBottom: '24px' }}>
              <label className="section-label">Dish Name</label>
              <input
                id="dish-name-input"
                type="text"
                className="input-custom"
                placeholder="e.g. Butter Chicken, Dal Makhani…"
                value={dishName}
                onChange={handleInputChange}
                autoComplete="off"
                aria-autocomplete="list"
                aria-controls="dish-suggestions"
                required
                style={{ paddingLeft: '44px' }}
              />
              <span style={{
                position: 'absolute',
                left: '14px',
                top: '38px',
                fontSize: '1.1rem',
                pointerEvents: 'none',
              }}>🔍</span>

              {suggestions.length > 0 && (
                <ul id="dish-suggestions" className="suggestions-dropdown" role="listbox" aria-label="Recipe suggestions" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {suggestions.map((suggestion) => (
                    <li key={suggestion.dishName} role="presentation">
                      <button type="button" role="option" aria-selected="false" onClick={() => { setDishName(suggestion.dishName); setSuggestions([]); }}>
                        🍴 {suggestion.dishName}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {error && (
              <div style={{
                padding: '14px 18px',
                background: 'var(--color-danger-light)',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: 'var(--radius-md)',
                color: '#991B1B',
                fontSize: '0.9rem',
                marginBottom: '20px',
              }}>
                ⚠️ {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="submit" className="btn-primary-custom">
                Get Recipe
              </button>
              <button type="button" className="btn-secondary-custom" onClick={onBack}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Recipe Result */}
      {isRecipeReceived && recipe && (
        <div>
          <button
            className="btn-ghost-custom"
            style={{ marginBottom: '20px' }}
            onClick={() => { setIsRecipeReceived(false); setRecipe(null); }}
          >
            &#8592; Search Again
          </button>
          <Recipe recipe={recipe} />
        </div>
      )}
    </div>
  );
}

export default DishInput;
